import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "../baseApi";

export type User = {
  phone: string;
  id: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  employeeId: string;
  plant: string;
  designation: string;
  mobileNumber?: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  plant: string;
  designation: string;
};

export type UpdateUserPayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  plant?: string;
  designation?: string;
};

export type GetUsersResponse = {
  message: string;
  users: User[];
  count: number;
  pagination: {
    totalPages: number;
    currentPage: number;
  };

  distinctPlants: string[];
  distinctRoles: string[];
};

export type GetUserByIdResponse = {
  message: string;
  user: User;
};

export type CreateUserResponse = {
  message: string;
  users: User;
};

export type UpdateUserResponse = {
  message: string;
  users: User;
};

export type DropdownOption = {
  id: number;
  label: string;
};

export type DropdownResponse = {
  message: string;
  data: DropdownOption[];
};
type GetUsersParams = {
  page?: number;
  limit?: number;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  plant: any;
  designation: any;
};

export const userManagementApi = createApi({
  reducerPath: "userManagementApi",
  baseQuery: createBaseQuery("user"),
  tagTypes: ["USERS", "USER_DETAIL"],
  endpoints: (builder) => ({
    getUsers: builder.query<GetUsersResponse, GetUsersParams | void>({
      query: (params) => ({
        url: "/all",
        params: {
          page: params?.page,
          limit: params?.limit,
          search: params?.search,
          sortField: params?.sortField,
          sortOrder: params?.sortOrder,
          plant: params?.plant,
          designation: params?.designation,
        },
      }),
      providesTags: ["USERS"],
    }),

    getUsersById: builder.query<GetUserByIdResponse, string>({
      query: (id) => `/${id}`,
    }),

    getAllPlants: builder.query<DropdownResponse, void>({
      query: () => `/plants`,
    }),

    getAllRoles: builder.query<DropdownResponse, void>({
      query: () => `/roles`,
    }),

    createUser: builder.mutation<CreateUserResponse, CreateUserPayload>({
      query: (body) => {
        const queryParams = new URLSearchParams({
          OUID: String(1),
        }).toString();

        return {
          url: `/create?${queryParams}`,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
        };
      },
      invalidatesTags: ["USERS"],
    }),

    editUser: builder.mutation<
      UpdateUserResponse,
      { id: string } & UpdateUserPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/update/${id}`,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body,
      }),
    }),

    deleteUser: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["USERS"],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUsersByIdQuery,
  useGetAllPlantsQuery,
  useGetAllRolesQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
} = userManagementApi;
